/**
 * The panel's top bar — §14.7's four "cheap versions", built on data the
 * platform already holds and adding no infrastructure.
 *
 * | Feature       | What it is here                                          |
 * |---------------|----------------------------------------------------------|
 * | Search        | `GET /admin/search`, one endpoint, existing indexes       |
 * | Notifications | the 60 s `/admin/summary` poll + a `localStorage` marker  |
 * | Clock         | `summary.server_time`, ticked client-side                 |
 * | Online fleet  | `summary.fleet`, shown only with `device.read`            |
 *
 * Explicitly NOT here: push, SSE, WebSockets, a `notifications` table, email, a
 * full-text index, a second heartbeat. Each of those is a piece of standing
 * infrastructure bought to render a number that a poll already returns.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LOCALES, type Locale } from '@navoiy/domain';
import type { components } from '@navoiy/api-types';
import { ApiError, api, unwrap } from '../api/client.js';
import { useSession } from '../auth/SessionContext.js';
import { useI18n } from '../i18n/LocaleContext.jsx';
import { formatClock, formatDate } from '../lib/format.js';
import { useSummary } from '../lib/useSummary.js';
import { Badge, Button } from '../ui/index.jsx';
import { AppellantMonogram } from './AppellantMonogram.jsx';

type SearchResponse = components['schemas']['AdminSearchResponse'];
type ListItem = components['schemas']['AdminMurojaatListItem'];

const LOCALE_LABEL: Record<Locale, string> = {
  'uz-Latn': 'shared.lang.code.uzLatn',
  'uz-Cyrl': 'shared.lang.code.uzCyrl',
  ru: 'shared.lang.code.ru',
  en: 'shared.lang.code.en',
};

/** Typing a name must not be a request per keystroke. */
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_MIN_CHARS = 2;

/**
 * §14.7 — the bell's acknowledgement marker, per user.
 *
 * Held in `localStorage` and nowhere else: "I have seen these" is a preference
 * of one person at one workstation, not a fact about the appeal, and giving it
 * a server column would make it a shared piece of state that one staff member
 * could clear for everybody.
 */
function ackKey(userId: string): string {
  return `nv.admin.attentionAck.${userId}`;
}

function readAck(userId: string): number {
  try {
    const raw = window.localStorage.getItem(ackKey(userId));
    const parsed = raw ? Date.parse(raw) : Number.NaN;
    return Number.isNaN(parsed) ? 0 : parsed;
  } catch {
    // A panel that cannot read localStorage still has to render; the cost is a
    // badge that does not remember it was dismissed.
    return 0;
  }
}

function writeAck(userId: string, iso: string): void {
  try {
    window.localStorage.setItem(ackKey(userId), iso);
  } catch {
    // Non-fatal: the acknowledgement simply does not survive a reload.
  }
}

export function TopBar(): React.JSX.Element {
  const { me, allows, logout } = useSession();
  const { t, locale, setLocale } = useI18n();
  const { summary, serverNow, error } = useSummary();
  const navigate = useNavigate();

  const [term, setTerm] = useState('');
  const [results, setResults] = useState<SearchResponse | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [ack, setAck] = useState(0);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (me) setAck(readAck(me.id));
  }, [me]);

  // Debounced search. `q` shorter than two characters is not sent at all: a
  // one-character `ILIKE '%a%'` over `author_name` is a table scan that returns
  // every citizen whose name contains an "a".
  useEffect(() => {
    const trimmed = term.trim();
    if (trimmed.length < SEARCH_MIN_CHARS) {
      setResults(null);
      setSearching(false);
      return;
    }
    setSearching(true);
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const data = unwrap(
            await api().GET('/admin/search', { params: { query: { q: trimmed, locale } } }),
          );
          setResults(data);
        } catch (caught) {
          // A failed search shows "nothing found" rather than an error banner
          // over the whole panel: the box is an accelerator, and a 422 on a
          // half-typed reference is not an incident.
          setResults(
            caught instanceof ApiError
              ? { murojaats: [], appellants: [], mahallas: [] }
              : null,
          );
        } finally {
          setSearching(false);
        }
      })();
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [term, locale]);

  // A click anywhere else closes the dropdowns.
  useEffect(() => {
    function onPointerDown(event: MouseEvent): void {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setSearchOpen(false);
      }
    }
    window.addEventListener('mousedown', onPointerDown);
    return () => window.removeEventListener('mousedown', onPointerDown);
  }, []);

  /**
   * §14.7 — the badge counts attention items NEWER than the acknowledgement
   * marker.
   *
   * "Newer" is by `received_at`, not by id: the ids are UUIDv4 and carry no
   * order at all, so an id-based marker would be a comparison of random
   * numbers. The marker stores the newest `received_at` the user has seen.
   */
  const attention: ListItem[] = useMemo(() => summary?.attention.overdue ?? [], [summary]);
  const unseen = attention.filter((item) => Date.parse(item.received_at) > ack);
  const unassigned = summary?.attention.unassigned_count ?? 0;

  function acknowledge(): void {
    if (!me || attention.length === 0) return;
    const newest = attention.reduce(
      (max, item) => (Date.parse(item.received_at) > Date.parse(max) ? item.received_at : max),
      attention[0]!.received_at,
    );
    writeAck(me.id, newest);
    setAck(Date.parse(newest));
  }

  function openAppeal(id: string): void {
    setSearchOpen(false);
    setTerm('');
    navigate(`/murojaats/${id}`);
  }

  const hasResults =
    results !== null &&
    (results.murojaats.length > 0 ||
      results.appellants.length > 0 ||
      results.mahallas.length > 0);

  return (
    <header className="flex flex-wrap items-center gap-3 border-b-1 border-line-200 bg-surface-0 px-6 py-3">
      {/* -- universal search (§14.2.7) ------------------------------------ */}
      <div ref={searchRef} className="relative min-w-64 flex-1">
        <input
          type="search"
          value={term}
          maxLength={64}
          onChange={(event) => {
            setTerm(event.target.value);
            setSearchOpen(true);
          }}
          onFocus={() => setSearchOpen(true)}
          placeholder={t('admin.topbar.searchPlaceholder')}
          aria-label={t('admin.topbar.searchPlaceholder')}
          className="w-full rounded-chip border-1 border-line-300 bg-surface-50 px-3 py-2 text-sm text-ink-900 outline-none transition-colors duration-fast focus:border-brand-500"
        />

        {searchOpen && term.trim().length >= SEARCH_MIN_CHARS ? (
          <div className="absolute left-0 top-full z-overlay mt-1 max-h-96 w-full min-w-96 overflow-y-auto rounded-card border-1 border-line-200 bg-surface-0 shadow-elev-3">
            {searching && !results ? (
              <p className="px-4 py-3 text-sm text-ink-500">{t('admin.common.loading')}</p>
            ) : null}

            {results && results.murojaats.length > 0 ? (
              <section>
                <h3 className="px-4 pt-3 text-xs font-semibold uppercase tracking-label text-ink-500">
                  {t('admin.topbar.searchGroupMurojaats')}
                </h3>
                <ul>
                  {results.murojaats.map((item) => (
                    <li key={item.id}>
                      <button
                        type="button"
                        onClick={() => openAppeal(item.id)}
                        className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm hover:bg-surface-50"
                      >
                        <span className="whitespace-nowrap font-semibold tracking-ref text-brand-800">
                          {item.reference}
                        </span>
                        <span className="truncate text-ink-500">{item.mahalla_name}</span>
                        <span className="ml-auto shrink-0">
                          <Badge tone={item.is_overdue ? 'danger' : 'neutral'}>
                            {t(`shared.murojaat.status.${item.status}`)}
                          </Badge>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {/*
              §14.2.6's group. Its rows are people, so it renders a monogram and
              never a photo, and an appellant with no `display_name` (a
              voice-only filer) shows an em dash rather than an invented name.
            */}
            {results && results.appellants.length > 0 ? (
              <section className="border-t-1 border-line-100">
                <h3 className="px-4 pt-3 text-xs font-semibold uppercase tracking-label text-ink-500">
                  {t('admin.topbar.searchGroupAppellants')}
                </h3>
                <ul>
                  {results.appellants.map((person) => (
                    <li key={person.appellant_id}>
                      <button
                        type="button"
                        onClick={() => {
                          setSearchOpen(false);
                          setTerm('');
                          navigate(`/appellants/${person.appellant_id}`);
                        }}
                        className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm hover:bg-surface-50"
                      >
                        <AppellantMonogram name={person.display_name} />
                        <span className="truncate text-ink-900">
                          {person.display_name ?? t('admin.appellants.noName')}
                        </span>
                        <span className="whitespace-nowrap text-ink-500">
                          ···{person.phone_tail}
                        </span>
                        <span className="ml-auto shrink-0">
                          <Badge tone={person.is_repeat ? 'gold' : 'neutral'}>
                            {t('admin.appellants.appealCountShort', {
                              count: person.appeal_count,
                            })}
                          </Badge>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {results && results.mahallas.length > 0 ? (
              <section className="border-t-1 border-line-100">
                <h3 className="px-4 pt-3 text-xs font-semibold uppercase tracking-label text-ink-500">
                  {t('admin.topbar.searchGroupMahallas')}
                </h3>
                <ul>
                  {results.mahallas.map((mahalla) => (
                    <li key={mahalla.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setSearchOpen(false);
                          navigate(`/murojaats?mahalla=${mahalla.id}`);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-ink-700 hover:bg-surface-50"
                      >
                        {mahalla.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {!searching && !hasResults ? (
              <p className="px-4 py-3 text-sm text-ink-500">{t('admin.topbar.searchEmpty')}</p>
            ) : null}

            {/*
              Says what the box searches — and, by omission, what it does not.
              §14.2.7 never searches an appeal's body or a transcript, and a
              staff member who does not know that will conclude the search is
              broken rather than deliberately bounded.
            */}
            <p className="border-t-1 border-line-100 px-4 py-2 text-xs text-ink-400">
              {t('admin.topbar.searchHint')}
            </p>
          </div>
        ) : null}
      </div>

      {/* -- server clock (§14.7) ------------------------------------------ */}
      <div className="text-right" title={t('admin.topbar.clockLabel')}>
        <p className="text-sm font-semibold tabular-nums text-ink-900">{formatClock(serverNow)}</p>
        <p className="text-xs text-ink-500">
          {serverNow ? formatDate(serverNow.toISOString()) : t('admin.topbar.clockLabel')}
        </p>
      </div>

      {/* -- online kiosks, only with `device.read` ------------------------ */}
      {allows('device.read') && summary?.fleet ? (
        <div className="text-right" title={t('admin.topbar.fleetLabel')}>
          <p className="text-sm font-semibold tabular-nums text-ink-900">
            {t('admin.topbar.fleetOnline', {
              online: summary.fleet.devices_online,
              total: summary.fleet.devices_total,
            })}
          </p>
          <p className="text-xs text-ink-500">{t('admin.topbar.fleetLabel')}</p>
        </div>
      ) : null}

      {/* -- the bell (§14.7) ---------------------------------------------- */}
      {allows('analytics.read') ? (
        <div className="relative">
          <Button
            tone="secondary"
            onClick={() => setBellOpen((open) => !open)}
            aria-label={t('admin.topbar.notifications')}
          >
            {t('admin.topbar.notifications')}
            {unseen.length > 0 ? (
              <span className="ml-1 rounded-chip bg-danger-500 px-2 py-0.5 text-xs font-bold text-surface-0">
                {unseen.length}
              </span>
            ) : null}
          </Button>

          {bellOpen ? (
            <div className="absolute right-0 top-full z-overlay mt-1 w-96 rounded-card border-1 border-line-200 bg-surface-0 shadow-elev-3">
              <header className="flex items-center justify-between border-b-1 border-line-200 px-4 py-2">
                <h3 className="text-sm font-semibold text-brand-900">
                  {t('admin.topbar.notifications')}
                </h3>
                <Button tone="ghost" onClick={acknowledge}>
                  {t('admin.topbar.notificationsAck')}
                </Button>
              </header>

              {unassigned > 0 ? (
                <p className="border-b-1 border-line-100 px-4 py-2 text-sm text-ink-700">
                  {t('admin.dashboard.attentionUnassigned', { count: unassigned })}
                </p>
              ) : null}

              {attention.length === 0 ? (
                <p className="px-4 py-3 text-sm text-ink-500">
                  {t('admin.topbar.notificationsEmpty')}
                </p>
              ) : (
                <ul>
                  {attention.map((item) => (
                    <li key={item.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setBellOpen(false);
                          navigate(`/murojaats/${item.id}`);
                        }}
                        className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm hover:bg-surface-50"
                      >
                        <span className="whitespace-nowrap font-semibold tracking-ref text-brand-800">
                          {item.reference}
                        </span>
                        <span className="truncate text-ink-500">{item.mahalla_name}</span>
                        <span className="ml-auto shrink-0 font-semibold text-danger-500">
                          {formatDate(item.due_at)}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* A stale poll is stated, not hidden: the numbers on screen are the last
          good ones, and the user is entitled to know they are not live. */}
      {error ? <Badge tone="danger">{t('admin.topbar.staleData')}</Badge> : null}

      <div className="flex items-center gap-1 rounded-btn bg-surface-100 p-1">
        {LOCALES.map((code) => (
          <button
            key={code}
            type="button"
            onClick={() => setLocale(code)}
            className={`rounded-chip px-2 py-1 text-xs font-semibold transition-colors duration-fast ${
              locale === code ? 'bg-surface-0 text-brand-800 shadow-elev-1' : 'text-ink-500'
            }`}
          >
            {t(LOCALE_LABEL[code])}
          </button>
        ))}
      </div>

      <Button tone="ghost" onClick={() => void logout()}>
        {t('admin.nav.logout')}
      </Button>
    </header>
  );
}
